import { useEffect, useState } from 'react';
import { hasActiveUploads } from '../modules/intakeLoop/helpers/hasActiveUploads';
import { useUploadStore } from '../store/uploadStore';
import { convertBytesPerSecondToMbps } from '../libs/convertBytesPerSecondToMbps';
import { Gold80, White } from '@traceair/tds';
import { NoInternetIcon } from '@traceair/webapp-icons';
import { BadConnectionIcon } from '../images/components/BadConnectionIcon';
import { AlertData } from '../store/alertStore';

interface NetworkStatusState {
  isOnline: boolean;
  isSlow: boolean; // Whether the connection is "slow" based on low upload speed
  isUnstable: boolean; // Whether the connection is "unstable" based on latency
  rtt?: number; // Round-trip time in ms
}

const CHECK_INTERVAL = 1000;
export const UPLINK_THRESHOLD = 5;
export const RTT_THRESHOLD = 400;

interface NetworkConnection extends EventTarget {
  rtt?: number;
}

/**
 * - Reports offline connection status
 * - Also reports slow and unstable connection status if there are active uploads
 */
const useNetworkStatus = (): NetworkStatusState => {
  const [status, setStatus] = useState<NetworkStatusState>(() => ({
    isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
    isSlow: false,
    isUnstable: false,
    rtt: undefined,
  }));

  useEffect(() => {
    const handleOnline = () => setStatus((prev) => ({ ...prev, isOnline: true }));
    const handleOffline = () =>
      setStatus({
        isOnline: false,
        isSlow: false,
        isUnstable: false,
        rtt: undefined,
      });

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const connection = (navigator as any).connection as NetworkConnection | undefined;

    if (!connection) {
      console.warn('The Network Information API is not supported in this browser. RTT checks are disabled.');
    }

    const intervalId = setInterval(() => {
      const { bytesPerSecond } = useUploadStore.getState();
      const megabitsPerSecond = bytesPerSecond ? convertBytesPerSecondToMbps(bytesPerSecond) : null;

      // We will only complain about slow or unstable internet during file uploads
      const ignoreNetworkIssues = !hasActiveUploads();

      const rtt = connection?.rtt;
      const isRttSupported = typeof rtt === 'number';

      setStatus((prevStatus) => ({
        ...prevStatus,
        isSlow: !ignoreNetworkIssues && !!megabitsPerSecond && megabitsPerSecond < UPLINK_THRESHOLD,
        isUnstable: !ignoreNetworkIssues && isRttSupported && rtt > RTT_THRESHOLD,
        rtt: isRttSupported ? rtt : undefined,
      }));
    }, CHECK_INTERVAL);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(intervalId);
    };
  }, []);

  return status;
};

export const useNetworkAlert = () => {
  const { isOnline, isSlow, isUnstable, rtt } = useNetworkStatus();
  const { bytesPerSecond } = useUploadStore();

  const megabitsPerSecond = bytesPerSecond ? convertBytesPerSecondToMbps(bytesPerSecond) : null;

  if (!isOnline) {
    return {
      title: 'No Internet Connection',
      description: 'Please check your connection.',
      icon: <NoInternetIcon color={White} />,
      type: 'warning-accent',
      unclosable: true,
    } as AlertData;
  }

  if (!isSlow && !isUnstable) {
    return null;
  }

  let description = '';
  if (isSlow && isUnstable) {
    description = `Slow upload speed (${megabitsPerSecond} Mbps) and high latency (${rtt} ms) detected. `;
  } else if (isSlow) {
    description = `Slow upload speed (${megabitsPerSecond} Mbps) detected. `;
  } else if (isUnstable) {
    description = `High latency (${rtt} ms) detected. `;
  }
  description += 'Please check your connection.';

  return {
    title: 'Poor Internet Connection',
    description,
    icon: <BadConnectionIcon color={Gold80} />,
    type: 'attention',
    unclosable: true,
  } as AlertData;
};
